import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { TokenPaymentMethod } from '@sorare/core/src/__generated__/globalTypes';
import type { ConfigQuery_exchangeRate } from '@sorare/core/src/contexts/config/types';
import {
  availableBalancesFragment,
  commonCardMarketplaceFragment,
} from '@sorare/core/src/contexts/currentUser/queries';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { SmallUser } from '#src/components/user/SmallUser/index.tsx';
import { useRetryOnExpiredExchangeRate } from '#src/hooks/useRetryOnExpiredExchangeRate.ts';
import { buildApplePayApprovals } from '#src/lib/buildApplePayApprovals.ts';

import type {
  AcceptOfferMutation,
  AcceptOfferMutationVariables,
  useAcceptOffer_anyCard,
} from './__generated__/useAcceptOffer.graphql.ts';
import { useApproveMigrator } from './useApproveMigrator.ts';
import { useMigrateCards } from './useMigrateCards.ts';
import { usePrepareAcceptOffer } from './usePrepareAcceptOffer.ts';

const ACCEPT_OFFER_MUTATION = gql`
  mutation AcceptOfferMutation($input: acceptOfferInput!, $sport: Sport!) {
    acceptOffer(input: $input) {
      deal {
        ... on TokenPrimaryOffer {
          id
          status
          endDate
          buyer {
            slug
            ...SmallUser_user
          }
          cards: anyCards {
            slug
            tokenOwner {
              id
              from
              transferType
              user {
                slug
              }
            }
          }
        }
        ... on TokenOffer {
          id
          status
          senderSide {
            id
            amounts {
              ...MonetaryAmountFragment_monetaryAmount
            }
            cards: anyCards {
              slug
              tokenOwner {
                id
                from
                transferType
              }
              liveSingleSaleOffer {
                id
              }
            }
          }
        }
        ... on TokenAuction {
          id
        }
      }
      currentUser {
        slug
        ...commonCardMarketplace_currentUser
        ...CurrentUserProvider_availableBalances
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${monetaryAmountFragment}
  ${SmallUser.fragments.user}
  ${availableBalancesFragment}
  ${commonCardMarketplaceFragment}
` as TypedDocumentNode<AcceptOfferMutation, AcceptOfferMutationVariables>;

type AcceptOfferArgs = {
  supportedCurrency: SupportedCurrency;
  offerId?: string;
  cartId?: string;
  purchasableItemId?: string;
  useAvailableCredits: boolean;
  receiveCards?: useAcceptOffer_anyCard[] | null;
  attemptReference: string | null;
  signedAmount?: string;
  tokenPaymentMethod?: TokenPaymentMethod;
  applePayPaymentToken?: ApplePayJS.ApplePayPaymentToken;
  afterPrepareAcceptOffer?: (offerId: string) => void;
};

export const useAcceptOffer = () => {
  const [acceptOffer] = useMutation(ACCEPT_OFFER_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const { showNotification } = useSnackNotificationContext();
  const approveMigrator = useApproveMigrator();
  const migrateCards = useMigrateCards();
  const { prepareAcceptOffer } = usePrepareAcceptOffer();
  const retryOnExpiredExchangeRate = useRetryOnExpiredExchangeRate();
  const { track } = useEventsContext();
  const { sport } = useSportContext();

  const prepareAndAcceptOffer = async ({
    exchangeRate,
    silenceErrors,
    offerId,
    cartId,
    purchasableItemId,
    receiveCards: receiveCardsInput,
    useAvailableCredits,
    supportedCurrency,
    attemptReference,
    signedAmount,
    tokenPaymentMethod = TokenPaymentMethod.WALLET,
    applePayPaymentToken,
    afterPrepareAcceptOffer,
  }: AcceptOfferArgs & {
    exchangeRate?: ConfigQuery_exchangeRate;
    silenceErrors: boolean;
  }) => {
    const receiveCards = receiveCardsInput || [];
    const isApplePay = tokenPaymentMethod === TokenPaymentMethod.APPLE_PAY;
    const {
      errors: prepareErrors,
      settlementInfo,
      approvals,
      authorizations,
      primaryOfferId,
    } = await prepareAcceptOffer({
      offerId,
      cartId,
      purchasableItemId,
      supportedCurrency,
      tokenPaymentMethod,
      useAvailableCredits,
      attemptReference,
      signedAmount,
      signAuthorizations: !isApplePay,
      exchangeRate,
      silenceErrors,
    });

    // Nothing was signed or charged yet, so the caller can retry this on a
    // fresh rate.
    if (prepareErrors.length > 0) return { errors: prepareErrors, cardIds: [] };

    const cardIds = receiveCards
      .map(card => ('id' in card ? card.id : undefined))
      .filter(Boolean);
    afterPrepareAcceptOffer?.(offerId || primaryOfferId || '');
    if (isApplePay) {
      if (!applePayPaymentToken) {
        sendSafeError(
          'Missing applePayPaymentToken for ApplePay payment method'
        );
        return null;
      }
      const applePayApprovals = buildApplePayApprovals({
        approvals: authorizations,
        applePayPaymentToken,
      });

      await approveMigrator(receiveCards);
      const migrationData = await migrateCards(receiveCards);

      const result = await acceptOffer({
        variables: {
          input: {
            offerId: offerId || primaryOfferId || '',
            migrationData,
            settlementInfo,
            approvals: applePayApprovals,
            signedAmount,
          },
          sport,
        },
        context: { silenceErrors },
      });
      track('Accept Direct Offer', {
        offer_id: offerId,
      });
      return {
        cardIds,
        result,
        errors: result.errors,
      };
    }

    if (!approvals) {
      showNotification('unlockWallet');
      throw new Error('Missing password');
    }
    await approveMigrator(receiveCards);
    const migrationData = await migrateCards(receiveCards);

    const result = await acceptOffer({
      variables: {
        input: {
          offerId: offerId || primaryOfferId || '',
          migrationData,
          settlementInfo,
          approvals,
          signedAmount,
        },
        sport,
      },
      context: { silenceErrors },
    });
    track('Accept Direct Offer', {
      offer_id: offerId || primaryOfferId,
      cart_id: cartId,
    });
    return {
      cardIds,
      result,
      errors: result.errors,
    };
  };

  return async (props: AcceptOfferArgs) =>
    retryOnExpiredExchangeRate(async ({ exchangeRate, silenceErrors }) => {
      const outcome = await prepareAndAcceptOffer({
        ...props,
        exchangeRate,
        silenceErrors,
      });
      const { result, cardIds, errors = [] } = outcome || {};

      const payloadErrors = result?.data?.acceptOffer?.errors ?? [];
      const failed = [...errors, ...payloadErrors];

      // Keep every message in the rendered list, dialog-gated ones included:
      // ApplePayButton infers success from an empty list, so emptying it here
      // completes the Apple Pay sheet on a payment that never happened.
      return {
        errors: failed,
        result: failed.length
          ? { errors: formatGqlErrors(failed) }
          : { deal: result?.data?.acceptOffer?.deal, cardIds },
      };
    });
};

useAcceptOffer.fragments = {
  anyCard: gql`
    fragment useAcceptOffer_anyCard on AnyCardInterface {
      slug
      sport
      ... on Card {
        slug
        id
      }
      ...useApproveMigrator_anyCard
      ...useMigrateCards_anyCard
    }
    ${useApproveMigrator.fragments.anyCard}
    ${useMigrateCards.fragments.anyCard}
  ` as TypedDocumentNode<useAcceptOffer_anyCard>,
};
